import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles as SparkIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STARTERS = [
  "Who is Prince Carmel?",
  "Tell me about the pixies",
  "What is the Whispering Glade?",
  "How can I find a fairy door?",
];

const AskPixie = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "pixie",
      text: "Hello, little explorer! ✿ I'm Pip, a tiny pixie from Miara Mountain. Ask me anything about our magical land — twinkle-twinkle!",
    },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const userText = (text ?? input).trim();
    if (!userText || sending) return;
    setMessages((m) => [...m, { role: "user", text: userText }]);
    setInput("");
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userText,
      });
      if (res.data?.session_id) setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: "pixie", text: res.data.reply }]);
    } catch (e) {
      console.error(e);
      setMessages((m) => [
        ...m,
        {
          role: "pixie",
          text: "Oh dear — I got swept up by a leaf-storm. Try whispering again in a moment? ✿",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen((o) => !o)}
        data-testid="ask-pixie-toggle"
        initial={{ scale: 0, rotate: -45 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", delay: 1.5, stiffness: 200 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 right-6 z-[60] w-16 h-16 rounded-full glow-pulse"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, #FFE89A 0%, #FFD700 50%, #B8861A 100%)",
          boxShadow:
            "0 10px 30px rgba(255,215,0,0.45), inset 0 1px 0 rgba(255,255,255,0.5)",
        }}
        aria-label="Ask the Pixie"
      >
        {open ? (
          <X size={26} className="mx-auto text-emerald-950" strokeWidth={2.5} />
        ) : (
          <SparkIcon size={26} className="mx-auto text-emerald-950" strokeWidth={2.5} />
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-28 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[400px] h-[560px] max-h-[80vh] glass-card flex flex-col overflow-hidden"
            data-testid="ask-pixie-panel"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-amber-500/15 flex items-center gap-3 bg-emerald-950/40">
              <div className="w-10 h-10 rounded-full bg-amber-400/20 border border-amber-400/40 flex items-center justify-center">
                <SparkIcon size={18} className="text-amber-300" />
              </div>
              <div>
                <div className="font-heading text-xl text-white leading-none">Pip the Pixie</div>
                <div className="font-accent text-amber-300/80 text-sm leading-none mt-1">
                  Always twinkling
                </div>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollerRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
              data-testid="ask-pixie-messages"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] text-sm leading-relaxed ${
                      m.role === "user" ? "chat-bubble-user" : "chat-bubble-pixie"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="chat-bubble-pixie text-sm italic opacity-80">
                    Pip is sprinkling stardust…
                  </div>
                </div>
              )}

              {messages.length <= 1 && (
                <div className="pt-3">
                  <div className="text-xs text-emerald-50/55 mb-2">Try asking:</div>
                  <div className="flex flex-wrap gap-2">
                    {STARTERS.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        data-testid={`ask-pixie-starter-${s.slice(0, 10).replace(/\s+/g, "-")}`}
                        className="text-xs px-3 py-1.5 rounded-full border border-amber-500/25 hover:border-amber-400/50 hover:bg-amber-400/5 text-emerald-50/85 hover:text-amber-200 transition"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="p-3 border-t border-amber-500/15 bg-emerald-950/40 flex gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Whisper to Pip…"
                disabled={sending}
                data-testid="ask-pixie-input"
                className="flex-1 bg-emerald-950/70 border border-amber-500/25 focus:border-amber-400/60 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-emerald-50/40 focus:outline-none transition-colors disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="ask-pixie-send"
                className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-300 to-amber-600 text-emerald-950 flex items-center justify-center disabled:opacity-50 hover:brightness-110 transition"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AskPixie;
